import React, { useContext, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useParams } from 'react-router';

import { t } from '@sm/intl';
import { StaticContext } from '@sm/webassets';
import { Typography } from '@wds/typography';
import { Box } from '@wds/box';
import { IconArrowRight } from '@wds/icons';

import { formatCostInLocaleCurrency, getCoreCostFromSkus, isPaidUser } from '~app/pages/Pricing/lib/lib-helpers';
import useTrackingDetails from '~pricing/hooks/useTrackingDetails';
import { COMPARISON_PACKAGES } from '~pricing/lib/constants';
import { PackageIds, Packages, ROOT_PRICING_ROUTE } from '~shared/constants/pricing';
import { NavRouteParams } from '~pricing/components/Navigation/types';
import { PackageType } from '~shared/types/pricing/package';
import { SummaryFooterPackages, SummaryDisclaimer } from './types';
import useTreatment from '~app/pages/Pricing/hooks/useTreatment';

import disclaimers, { LegaleseDisclaimer } from './config/disclaimers';

import usePricingFooterStyles from './useStyles';

import PricingFeatures from './PricingFeatures';
import COPY from './copy';
import MoreAbout from './MoreAbout';
import { usePricingExperiments } from '~shared/hooks/usePricingExperiments';
import { EXPERIMENT_IDENTIFIERS } from '~server/routes/Pricing/experimentsConfig';
import { GROW_2792_DISCLAIMERS } from '../../experiments/grow_2792_pnp_refresh_v1/constants';
import { usePricingState } from '~shared/hooks/usePricingState';

// List of country codes where we allow the educational discount
const EDU_COUNTRIES = ['US', 'GB'];

// List of packages to not display in the footer FAQ
// TODO @sking please deal with this as you see fit once forms package goes live after experiment
const HIDDEN_FAQ_PACKAGES = [
  Packages.StarterAnnual,
  Packages.Enterprise,
  Packages.Flex,
  Packages.FormsAnnual,
  Packages.FormsMonthly,
];

export default function PricingFooter({ packages }: SummaryFooterPackages): React.ReactElement {
  const {
    user,
    environment: { countryCode },
  } = useContext(StaticContext);
  const {
    state: {
      packages: { overageCost, getPackageById },
    },
  } = usePricingState();
  const { canonicalPath, subPath } = useParams<NavRouteParams>();
  const { pageUtSource } = useTrackingDetails();
  const { priceExperiment } = usePricingExperiments();

  const isPnPRefresh =
    priceExperiment.hasAssignment && priceExperiment.treatmentName === EXPERIMENT_IDENTIFIERS.GROW_2792.treatment;

  const disclaimersMap = isPnPRefresh ? GROW_2792_DISCLAIMERS : disclaimers;

  const {
    uiExperiment: { GROW_2529_FORMS_TREATMENT1, GROW_2529_FORMS_TREATMENT2, GROW_2629_FORMS_V2_TREATMENT },
  } = useTreatment();

  const GROW_2529_FORMS = GROW_2629_FORMS_V2_TREATMENT || GROW_2529_FORMS_TREATMENT1 || GROW_2529_FORMS_TREATMENT2;

  const { Audience, Disclaimers, Disclaimer, Footer, GetStarted, More, SeeAllLink } = usePricingFooterStyles();

  const displayPackages = useMemo(() => {
    const filteredPackages = packages.filter(pkg => !HIDDEN_FAQ_PACKAGES.includes(pkg.packageName));
    // Reverse the packages cost FAQ list for EDU page.
    if (canonicalPath === PackageType.Educational) {
      filteredPackages.reverse();
    }
    return filteredPackages;
  }, [packages, canonicalPath]);
  // for the sales form link
  const utSource2 = pageUtSource?.includes('summary') ? 'summary' : 'details';
  const utSource3 = `${utSource2}_bottom_${user.isAuthenticated ? 'li' : 'lo'}`;

  const path = canonicalPath || PackageType.Teams;
  const displayEduFaq =
    !isPaidUser(user?.package) && path !== PackageType.Educational && EDU_COUNTRIES.includes(countryCode);
  // we hide the teams FAQ only on teams details and, subsequently, canonical details
  const displayTeamsDiscountFAQ =
    !(path === PackageType.Teams && subPath) && path !== 'details' && path !== PackageType.Educational;

  // this value represents the lowest percentage discount of all teams <-> individual packages,
  // rounded down to the nearest ten. it's used for legalese underneath the teams packages
  const teamsDiscountPercent = useMemo<number>(() => {
    const discountPercent = Object.keys(COMPARISON_PACKAGES).reduce<number>((minimumPercent, teamPackageName) => {
      let newMinimumPercent = minimumPercent;

      const comparisonPackageName = COMPARISON_PACKAGES[teamPackageName as Packages];
      if (comparisonPackageName) {
        const teamPricingPackage = getPackageById(PackageIds[teamPackageName as Packages]);
        const comparisonPricingPackage = getPackageById(PackageIds[comparisonPackageName]);
        const teamSku = getCoreCostFromSkus(teamPricingPackage?.skuCost);
        const comparisonSku = getCoreCostFromSkus(comparisonPricingPackage?.skuCost);

        if (teamSku && comparisonSku) {
          const packagePercent = Math.floor(100 - (teamSku.cost / comparisonSku.cost) * 100);
          if (packagePercent < newMinimumPercent) {
            newMinimumPercent = packagePercent;
          }
        }
      }

      return newMinimumPercent;
    }, 100);

    return Math.floor(discountPercent / 10) * 10;
  }, [getPackageById]);

  const conditionalPath = !isPnPRefresh ? `${path}/` : '';
  const featuresLink = `${ROOT_PRICING_ROUTE}/${conditionalPath}details?ut_source=${pageUtSource}`;

  return (
    <footer className={Footer} data-testid="PricingFooter">
      <ul className={Disclaimers}>
        {disclaimersMap[path as keyof SummaryDisclaimer]?.map((disclaimer: string) => {
          if (disclaimer === LegaleseDisclaimer.EnterpriseAddons && subPath) {
            return null;
          }
          let formattedOverageCost = null;
          if (disclaimer === LegaleseDisclaimer.OverageCost && overageCost) {
            formattedOverageCost = formatCostInLocaleCurrency(overageCost, 2);
          }

          return (
            <li className={Disclaimer} key={disclaimer} data-testid={`PricingFooter__${disclaimer}`}>
              {formattedOverageCost && <span id="billable-responses" />}
              <Typography variant="bodySm">
                {t(COPY[disclaimer as keyof typeof COPY], {
                  overageCost: formattedOverageCost,
                  teamsMinimumDiscount: teamsDiscountPercent,
                })}
              </Typography>
            </li>
          );
        })}
      </ul>

      {/**
       *
       * The Audience portion of the footer should only be rendered on Summary pages.
       * subPath is required to to determine if we are on the details page or not.
       * path can be either 'educational' (details view) or 'teams', or 'individual' (summary view)
       */}
      {path !== PackageType.Educational && !subPath && (
        <div className={Audience}>
          <PricingFeatures />

          {!user.isAuthenticated && (
            <Box className={GetStarted} data-testid="PricingFooter__SignUp">
              <Typography component="p" variant="body" align="center">
                {t(COPY.BASICS)} <Link to="/user/sign-up/?ut_source=pricing_page">{t(COPY.GET_STARTED)}</Link>
              </Typography>
            </Box>
          )}

          <Typography component="h3" align="center">
            <Link className={SeeAllLink} to={featuresLink}>
              {t(COPY.ALL_FEATURES)} <IconArrowRight size="sm" title="Arrow Right" />
            </Link>
          </Typography>
        </div>
      )}

      <Box className={More}>
        <Typography component="h4" variant="sectionTitle" align="center">
          {t(COPY.MORE)}
        </Typography>
      </Box>
      {/* if we are in forms treatment, show that treatment */}
      {GROW_2529_FORMS && (
        <GROW_2529_FORMS.MoreAbout
          {...{
            displayTeamsDiscountFAQ,
            pageUtSource,
            teamsDiscountPercent,
            utSource2,
            utSource3,
            displayEduFaq,
          }}
        />
      )}
      {/* if we are not in the treatment, show the default */}
      {!GROW_2529_FORMS && (
        <MoreAbout
          {...{
            displayEduFaq,
            displayPackages,
            displayTeamsDiscountFAQ,
            pageUtSource,
            teamsDiscountPercent,
            utSource2,
            utSource3,
          }}
        />
      )}
    </footer>
  );
}
